import { useState, useEffect, useMemo } from "react";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { Layers, Loader2, Plus, Save, Trash2, Euro } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Static set of plan_keys that ship with the seeder. We block deletion of
// these so the admin can't accidentally orphan venues that depend on them.
const DEFAULT_PLAN_KEYS = new Set(["free_listing", "verified_profile", "booking", "booking_pro"]);

export default function AdminVenuePlans() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [plans, setPlans] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // currently expanded plan_key
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [plansRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/venue-plans`),
        axios.get(`${API}/venue-plans/feature-catalogue`),
      ]);
      setPlans(plansRes.data);
      setCatalogue(catRes.data.toggles || []);
    } catch {
      toast.error(t("admin_venue_plans:load_failed", { defaultValue: "Failed to load plans" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const handleSave = async (plan_key, patch) => {
    try {
      const res = await axios.put(`${API}/admin/venue-plans/${plan_key}`, patch);
      setPlans(plans.map(p => p.plan_key === plan_key ? res.data : p));
      toast.success(t("admin_venue_plans:toast_saved", { defaultValue: "Plan saved" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    }
  };

  const handleDelete = async (plan_key) => {
    if (DEFAULT_PLAN_KEYS.has(plan_key)) {
      toast.error(t("admin_venue_plans:cannot_delete_default", { defaultValue: "Default plans cannot be deleted" }));
      return;
    }
    if (!window.confirm(t("admin_venue_plans:confirm_delete", { defaultValue: "Delete this plan? Venues on it will revert to Free Listing." }))) return;
    try {
      await axios.delete(`${API}/admin/venue-plans/${plan_key}`);
      setPlans(plans.filter(p => p.plan_key !== plan_key));
      toast.success(t("admin_venue_plans:toast_deleted", { defaultValue: "Plan deleted" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div data-testid="admin-venue-plans-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <Layers className="h-6 w-6 text-primary" />
          {t("admin_venue_plans:title", { defaultValue: "Venue Plans" })}
        </h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="venue-plans-create-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t("admin_venue_plans:add_plan", { defaultValue: "Add Plan" })}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("admin_venue_plans:create_title", { defaultValue: "Create Plan" })}</DialogTitle>
            </DialogHeader>
            <CreatePlanForm
              catalogue={catalogue}
              onCreated={(p) => {
                setPlans([...plans, p].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
                setShowCreate(false);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        {t("admin_venue_plans:intro", {
          defaultValue:
            "Plans control which features each venue can access. Pricing is in euros (€). Toggle features on/off and click Save. Default plans cannot be deleted.",
        })}
      </p>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-32 rounded-xl" />)}
        </div>
      ) : (
        <div className="space-y-4">
          {plans.map((p) => (
            <PlanCard
              key={p.plan_key}
              plan={p}
              catalogue={catalogue}
              expanded={editing === p.plan_key}
              onToggleExpand={() => setEditing(editing === p.plan_key ? null : p.plan_key)}
              onSave={(patch) => handleSave(p.plan_key, patch)}
              onDelete={() => handleDelete(p.plan_key)}
              isDefault={DEFAULT_PLAN_KEYS.has(p.plan_key)}
              lang={lang}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, catalogue, expanded, onToggleExpand, onSave, onDelete, isDefault, lang }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(plan);
  const [saving, setSaving] = useState(false);

  // Reset local edit state when collapsed or when plan refreshes from server.
  useEffect(() => { setForm(plan); }, [plan]);

  const groups = useMemo(() => {
    const out = {};
    catalogue.forEach(c => {
      out[c.group] = out[c.group] || [];
      out[c.group].push(c);
    });
    return out;
  }, [catalogue]);

  const enabledCount = Object.values(form.feature_toggles || {}).filter(Boolean).length;

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      name: form.name, name_el: form.name_el,
      description: form.description, description_el: form.description_el,
      price_monthly_eur: parseFloat(form.price_monthly_eur) || 0,
      price_yearly_eur: parseFloat(form.price_yearly_eur) || 0,
      sort_order: parseInt(form.sort_order || 0, 10),
      is_active: !!form.is_active,
      feature_toggles: form.feature_toggles || {},
    });
    setSaving(false);
  };

  const toggleFeature = (key) => {
    setForm({
      ...form,
      feature_toggles: { ...(form.feature_toggles || {}), [key]: !form.feature_toggles?.[key] },
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden" data-testid={`venue-plan-${plan.plan_key}`}>
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between p-5 hover:bg-muted/30 transition-colors text-left"
        data-testid={`venue-plan-toggle-${plan.plan_key}`}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Layers className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-foreground">
              {(lang === "el" && plan.name_el) || plan.name}
              <span className="ml-2 text-xs text-muted-foreground font-mono">{plan.plan_key}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              €{plan.price_monthly_eur}/{t("admin_venue_plans:month", { defaultValue: "month" })}
              {" · "}
              {enabledCount}/{catalogue.length} {t("admin_venue_plans:features_on", { defaultValue: "features on" })}
              {!plan.is_active && <span className="ml-2 px-1.5 py-0.5 bg-muted rounded text-xs">inactive</span>}
            </p>
          </div>
        </div>
        <span className="text-muted-foreground text-sm">{expanded ? "−" : "+"}</span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-5 border-t border-border pt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>{t("admin_venue_plans:field_name", { defaultValue: "Name (EN)" })}</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("admin_venue_plans:field_name_el", { defaultValue: "Name (EL)" })}</Label>
              <Input value={form.name_el || ""} onChange={(e) => setForm({ ...form, name_el: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Euro className="h-3.5 w-3.5" />{t("admin_venue_plans:price_monthly", { defaultValue: "Monthly price (€)" })}</Label>
              <Input type="number" min="0" step="1" value={form.price_monthly_eur ?? 0}
                onChange={(e) => setForm({ ...form, price_monthly_eur: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Euro className="h-3.5 w-3.5" />{t("admin_venue_plans:price_yearly", { defaultValue: "Yearly price (€)" })}</Label>
              <Input type="number" min="0" step="1" value={form.price_yearly_eur ?? 0}
                onChange={(e) => setForm({ ...form, price_yearly_eur: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("admin_venue_plans:sort_order", { defaultValue: "Sort order" })}</Label>
              <Input type="number" value={form.sort_order ?? 0}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1" />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid={`venue-plan-active-${plan.plan_key}`} />
              <span className="text-sm">{t("admin_venue_plans:active_label", { defaultValue: "Active (visible on pricing page)" })}</span>
            </div>
          </div>

          <div>
            <Label>{t("admin_venue_plans:field_description", { defaultValue: "Description (EN)" })}</Label>
            <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>{t("admin_venue_plans:field_description_el", { defaultValue: "Description (EL)" })}</Label>
            <Input value={form.description_el || ""} onChange={(e) => setForm({ ...form, description_el: e.target.value })} className="mt-1" />
          </div>

          <div>
            <p className="text-sm font-medium text-foreground mb-3">
              {t("admin_venue_plans:feature_toggles", { defaultValue: "Feature toggles" })} ({enabledCount}/{catalogue.length})
            </p>
            {Object.entries(groups).map(([groupName, items]) => (
              <div key={groupName} className="mb-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{groupName}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {items.map((tog) => (
                    <label key={tog.key}
                      className="flex items-center gap-3 p-2 rounded-lg border border-border hover:bg-muted/30 cursor-pointer"
                      data-testid={`venue-plan-toggle-${plan.plan_key}-${tog.key}`}
                    >
                      <Switch
                        checked={!!form.feature_toggles?.[tog.key]}
                        onCheckedChange={() => toggleFeature(tog.key)}
                      />
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{tog.label}</p>
                        <p className="text-[11px] text-muted-foreground font-mono truncate">{tog.key}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" onClick={handleSave} disabled={saving}
              data-testid={`venue-plan-save-${plan.plan_key}`}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {t("admin_venue_plans:save", { defaultValue: "Save plan" })}
            </Button>
            {!isDefault && (
              <Button size="sm" variant="outline" onClick={onDelete}
                className="text-destructive hover:text-destructive"
                data-testid={`venue-plan-delete-${plan.plan_key}`}>
                <Trash2 className="h-4 w-4 mr-1" />
                {t("admin_venue_plans:delete", { defaultValue: "Delete" })}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CreatePlanForm({ catalogue, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    plan_key: "", name: "", name_el: "",
    description: "", description_el: "",
    price_monthly_eur: 0, price_yearly_eur: 0, sort_order: 99,
    is_active: true,
    feature_toggles: Object.fromEntries(catalogue.map(c => [c.key, false])),
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/venue-plans`, {
        ...form,
        price_monthly_eur: parseFloat(form.price_monthly_eur) || 0,
        price_yearly_eur: parseFloat(form.price_yearly_eur) || 0,
        sort_order: parseInt(form.sort_order || 0, 10),
      });
      toast.success(t("admin_venue_plans:toast_created", { defaultValue: "Plan created" }));
      onCreated(res.data);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="create-venue-plan-form">
      <div>
        <Label>{t("admin_venue_plans:field_key", { defaultValue: "Plan key (no spaces, lowercase)" })} *</Label>
        <Input
          value={form.plan_key}
          onChange={(e) => setForm({ ...form, plan_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
          required className="mt-1 font-mono" placeholder="custom_tier"
          data-testid="create-venue-plan-key"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{t("admin_venue_plans:field_name", { defaultValue: "Name (EN)" })} *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" />
        </div>
        <div>
          <Label>{t("admin_venue_plans:field_name_el", { defaultValue: "Name (EL)" })}</Label>
          <Input value={form.name_el} onChange={(e) => setForm({ ...form, name_el: e.target.value })} className="mt-1" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{t("admin_venue_plans:price_monthly", { defaultValue: "Monthly (€)" })}</Label>
          <Input type="number" min="0" value={form.price_monthly_eur}
            onChange={(e) => setForm({ ...form, price_monthly_eur: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>{t("admin_venue_plans:price_yearly", { defaultValue: "Yearly (€)" })}</Label>
          <Input type="number" min="0" value={form.price_yearly_eur}
            onChange={(e) => setForm({ ...form, price_yearly_eur: e.target.value })} className="mt-1" />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("admin_venue_plans:create_hint", {
          defaultValue: "All toggles start OFF. Open the plan after creation to flip features on.",
        })}
      </p>
      <Button type="submit" disabled={loading} className="w-full" data-testid="create-venue-plan-submit">
        {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
        {t("admin_venue_plans:create", { defaultValue: "Create plan" })}
      </Button>
    </form>
  );
}
